// ignore-string-externalization

import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import {
  screenXsMax,
  screenSmMax,
  spacer16,
  spacer24,
  spacer40,
  cta1FontWeight,
  heading2FontSize,
  heading2LineHeightSmall,
  screenSmMin,
  screenMdMax,
  spacer8,
} from '@spotify-internal/encore-web-v5';

import { ContentGrid } from '../../ContentGrid';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { Headline, Supertext, Link, ImageAspectBox16x9 } from '../../ui';
import { ModuleType } from '../types';

import { ContentfulAsset } from '../../Contentful';
import { transformScaleUp, withoutProps } from '../../utils';
import { Colors } from '../../ui/colorsType';
import { hexToRgba } from '../../../../features/src/color-conversion';
import { MODULE_THEMES } from '../../Theme';
import { itemToSearchHit } from '../../Search';
import type { SearchHit } from '../../Search/types';
import { useLocale } from '@spotify-internal/i18n-core';
import { Grid } from '@spotify-internal/frodor-modules';

const FeaturedContent = styled.div`
  & > div > div,
  ul {
    padding-bottom: 0;
  }

  & > div > div > ul > li {
    margin-bottom: ${spacer24};
  }

  & > div:first-child > div > p {
    font-size: ${heading2FontSize};
    line-height: ${heading2LineHeightSmall};
    font-weight: ${cta1FontWeight};
    letter-spacing: -1.28px;
    text-transform: none;
    padding-top: ${spacer8};
  }
`;

const Hero = styled(Grid)<{ background: string; color: string }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding-top: ${spacer40};
  padding-bottom: ${spacer40};
  color: ${({ color }) => color};
  background-color: ${({ background }) => background};

  &:hover {
    p,
    h1 {
      color: ${props => hexToRgba(props.color, 0.75)};
    }
    img {
      ${transformScaleUp};
      transition: transform 0.3s ease-in-out;
    }
  }

  @media (max-width: ${screenXsMax}) {
    flex-direction: column;
  }
`;

const StyledLink = styled(withoutProps(Link, 'textColor'))<{
  textColor: string;
}>`
  color: ${({ textColor }) => textColor};

  @media (max-width: ${screenSmMax}) {
    & h1 {
      line-height: 36px;
    }
  }

  &&:hover:not(:disabled),
  &&:focus:not(:disabled) {
    color: ${(props: Colors) => hexToRgba(props.textColor || '', 0.75)};
  }
`;

const ImageContainer = styled(ImageAspectBox16x9)`
  height: 100%;
  width: 120%;
  order: 3;
  overflow: hidden;

  @media (max-width: ${screenXsMax}) {
    width: 100%;
    order: 2;
  }
`;

const TextContainer = styled.div`
  order: 2;
  padding-right: ${spacer40};
  align-self: center;
  height: 100%;
  width: 80%;

  & h1 {
    padding-bottom: 0;
  }

  @media (max-width: ${screenXsMax}) {
    padding-top: ${spacer16};
    width: 100%;
    order: 3;
  }
`;

const StyledHeadline = styled(Headline)`
  @media (min-width: ${screenSmMin}) and (max-width: ${screenMdMax}) {
    padding-bottom: ${spacer16};
  }
`;

const StyledSupertext = styled(Supertext)`
  padding-bottom: ${spacer16};
`;

const TRACK_CAT = 'module-featuredContent';

type FeaturedContentModuleType = {
  color?: string;
  background?: string;
  caption?: string;
  headline?: string;
  image?: ContentfulAsset;
  items?: Array<ModuleType>;
  gridSupertext?: string;
  heroSupertext?: string;
  theme?: string;
  articleLink?: ModuleType;
};

export const FeaturedContentModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  FeaturedContentModuleType
>(
  (
    {
      color = '',
      background = '',
      caption = '',
      items = [],
      gridSupertext = '',
      heroSupertext = '',
      theme = '',
      articleLink,
    },
    ref,
  ) => {
    const locale = useLocale();
    const hero = itemToSearchHit(articleLink, locale) as NonNullable<SearchHit>;

    const validatedItems = items.filter(i => i?.contentType, true).slice(0, 3);
    return (
      <FeaturedContent>
        <ThemeProvider theme={MODULE_THEMES[theme]}>
          <ContentGrid
            ref={ref}
            trackCategory={TRACK_CAT}
            data-testid={`featuredContent-module-${'id'}`}
            items={validatedItems}
            supertext={gridSupertext}
          />
        </ThemeProvider>
        {hero && (
          <StyledLink
            textColor={color}
            href={hero.url}
            track={[TRACK_CAT, `CTA Link - ${hero.title}`]}
          >
            <Hero color={color} background={background}>
              <TextContainer>
                <StyledSupertext>{heroSupertext}</StyledSupertext>
                {hero.title && <StyledHeadline>{hero.title}</StyledHeadline>}
              </TextContainer>
              <ImageContainer>
                {hero.image && (
                  <ContentfulImage asset={hero.image} caption={caption} />
                )}
              </ImageContainer>
            </Hero>
          </StyledLink>
        )}
      </FeaturedContent>
    );
  },
);
